// src/theme.ts
//
// 전역 디자인 시스템 — 클린 뉴트럴 + 단일 액센트.
//
//  · 바탕 #F6F7F8 / 서피스 #FFFFFF / 보더 #E4E7EA / 잉크 #171A1E
//  · 액센트는 카고 오렌지 #E2451C 하나만 사용(호버 #C93A14)
//  · 상태 의미색(steel·amber·signal·stop)은 유지 — 데이터 상태 표기 전용
//  · 라운드 8(컨트롤) / 12(카드·다이얼로그), 그림자는 미세하게만
//
// 시각 언어의 정본은 HomePage.jsx · LoginPage.tsx · DashboardLayout.tsx 이며
// 이 파일은 그 언어를 앱 전체 MUI 표면에 확장한 것이다.
//
// (구 "Cargo Manifest Console" 의 다크 슬랩 헤더, hazard tape, mono 대문자
//  라벨, radius 0, 3px 오프셋 그림자, 호버 translate 는 모두 제거되었다.)

import { createTheme } from '@mui/material/styles';
import type { ThemeOptions, Shadows } from '@mui/material/styles';

const SANS = [
  '"IBM Plex Sans KR"',
  '-apple-system',
  'BlinkMacSystemFont',
  '"Segoe UI"',
  'Roboto',
  '"Noto Sans KR"',
  'sans-serif',
].join(',');

const DISPLAY = [
  '"Archivo"',
  '"IBM Plex Sans KR"',
  '-apple-system',
  '"Segoe UI"',
  'sans-serif',
].join(',');

// mono 는 "대문자 장식 라벨" 용도로는 쓰지 않는다.
// 숫자·날짜·금액 등 tabular 데이터 표기 전용.
const MONO = [
  '"JetBrains Mono"',
  '"IBM Plex Mono"',
  'ui-monospace',
  'SFMono-Regular',
  'Menlo',
  'Consolas',
  'monospace',
].join(',');

/* ------------------------------------------------------------------ */
/* 원색 상수 — 아래 오버라이드 전부가 이 값만 참조한다                  */
/* ------------------------------------------------------------------ */

const INK = '#171A1E';
const SUB = '#6B7178';
const CAPTION = '#9AA0A6';

const CANVAS = '#F6F7F8';
const SURFACE = '#FFFFFF';
const FIELD = '#F9FAFB'; // 인풋/테이블 헤더 배경
const NEUTRAL = '#F2F4F5'; // 중립 칩 / 호버

const BORDER = '#E4E7EA';
const BORDER_STRONG = '#D7DBDF';
const ROW_LINE = '#EDEFF1';

const ACCENT = '#E2451C';
const ACCENT_HOVER = '#C93A14';
const ACCENT_TINT = '#FDEDE8';

// 상태 의미색 (유지)
const STEEL = '#0E5C6F';
const AMBER = '#E0A24A';
const SIGNAL = '#2C8A4A';
const STOP = '#B12B2B';

const SHADOW_XS = '0 1px 2px rgba(16,24,32,0.06)';
const SHADOW_SM = '0 2px 6px rgba(16,24,32,0.06)';
const SHADOW_MD = '0 8px 24px rgba(16,24,32,0.06)';
const SHADOW_POP = '0 8px 24px rgba(16,24,32,0.08)';
const SHADOW_DIALOG = '0 8px 24px rgba(16,24,32,0.12)';

const RADIUS_CONTROL = 8;
const RADIUS_SURFACE = 12;
const RADIUS_MENU = 10;
const RADIUS_CHIP = 8;

/* ------------------------------------------------------------------ */
/* 팔레트                                                              */
/* ------------------------------------------------------------------ */

const palette = {
  // 카고 오렌지 — 단일 액센트
  primary: {
    main: ACCENT,
    light: '#FF6B45',
    dark: ACCENT_HOVER,
    contrastText: '#FFFFFF',
  },
  // 스틸 틸 — 보조 / "진행·발행" 상태
  secondary: {
    main: STEEL,
    light: '#1B7E94',
    dark: '#073D49',
    contrastText: '#FFFFFF',
  },
  error:   { main: STOP,   dark: '#8E2222', light: '#D14545', contrastText: '#FFFFFF' },
  warning: { main: AMBER,  dark: '#B07A23', light: '#F0BC72', contrastText: '#1A1208' },
  success: { main: SIGNAL, dark: '#1F6535', light: '#4FAB6C', contrastText: '#FFFFFF' },
  info:    { main: STEEL,  dark: '#073D49', light: '#1B7E94', contrastText: '#FFFFFF' },
  background: {
    default: CANVAS,
    paper: SURFACE,
  },
  text: {
    primary: INK,
    secondary: SUB,
    disabled: CAPTION,
  },
  divider: BORDER,
  // 쿨 뉴트럴 스케일 (구 웜 뉴트럴 대체)
  grey: {
    50:  FIELD,
    100: CANVAS,
    200: NEUTRAL,
    300: BORDER,
    400: CAPTION,
    500: SUB,
    600: '#4A5057',
    700: '#343A40',
    800: '#23272B',
    900: INK,
  },
} as const;

/* ------------------------------------------------------------------ */
/* 그림자 — MUI 기본(짙은 회청색) 대신 미세 그림자로 통일               */
/* ------------------------------------------------------------------ */

const shadows = [
  'none',
  SHADOW_XS,
  '0 1px 3px rgba(16,24,32,0.06)',
  SHADOW_SM,
  '0 4px 10px rgba(16,24,32,0.06)',
  '0 6px 16px rgba(16,24,32,0.06)',
  SHADOW_MD,
  ...Array(18).fill(SHADOW_POP),
] as unknown as Shadows;

/* ------------------------------------------------------------------ */
/* 컴포넌트 오버라이드                                                 */
/* ------------------------------------------------------------------ */

const components: ThemeOptions['components'] = {
  MuiCssBaseline: {
    styleOverrides: {
      body: {
        backgroundColor: CANVAS,
        color: INK,
        fontFamily: SANS,
        WebkitFontSmoothing: 'antialiased',
        // 숫자·날짜·금액 정렬용 (mono 장식 라벨과는 무관)
        fontVariantNumeric: 'tabular-nums',
      },
      '::selection': {
        backgroundColor: ACCENT_TINT,
        color: INK,
      },
      '*::-webkit-scrollbar': { width: 10, height: 10 },
      '*::-webkit-scrollbar-track': { background: 'transparent' },
      '*::-webkit-scrollbar-thumb': {
        background: '#D7DBDF',
        borderRadius: 8,
        border: '2px solid transparent',
        backgroundClip: 'padding-box',
      },
      '*::-webkit-scrollbar-thumb:hover': {
        background: CAPTION,
        borderRadius: 8,
        border: '2px solid transparent',
        backgroundClip: 'padding-box',
      },
    },
  },

  MuiButton: {
    defaultProps: { disableElevation: true },
    styleOverrides: {
      root: {
        borderRadius: RADIUS_CONTROL,
        fontFamily: SANS,
        fontWeight: 600,
        textTransform: 'none',
        letterSpacing: 0,
        padding: '8px 16px',
        transition:
          'background-color 150ms ease, border-color 150ms ease, box-shadow 150ms ease, color 150ms ease',
      },
      contained: {
        boxShadow: SHADOW_XS,
        '&:hover': { boxShadow: SHADOW_SM },
        '&:active': { boxShadow: SHADOW_XS },
        '&.Mui-disabled': {
          boxShadow: 'none',
          backgroundColor: NEUTRAL,
          color: CAPTION,
        },
      },
      containedPrimary: {
        backgroundColor: ACCENT,
        color: '#FFFFFF',
        '&:hover': { backgroundColor: ACCENT_HOVER },
      },
      containedSecondary: {
        backgroundColor: STEEL,
        color: '#FFFFFF',
        '&:hover': { backgroundColor: '#073D49' },
      },
      outlined: {
        backgroundColor: SURFACE,
        border: `1px solid ${BORDER}`,
        color: INK,
        '&:hover': {
          border: `1px solid ${BORDER_STRONG}`,
          backgroundColor: NEUTRAL,
        },
        '&.Mui-disabled': {
          border: `1px solid ${BORDER}`,
          color: CAPTION,
        },
      },
      // 의미색 아웃라인 버튼은 텍스트 색만 되살린다(보더는 중립 유지).
      outlinedSecondary: { color: STEEL },
      outlinedInfo: { color: STEEL },
      outlinedSuccess: { color: SIGNAL },
      outlinedWarning: { color: '#B07A23' },
      outlinedError: { color: STOP },
      text: {
        color: INK,
        '&:hover': { backgroundColor: NEUTRAL },
      },
      textPrimary: {
        color: ACCENT,
        '&:hover': { backgroundColor: ACCENT_TINT },
      },
      textSecondary: { color: STEEL },
      textInfo: { color: STEEL },
      textSuccess: { color: SIGNAL },
      textWarning: { color: '#B07A23' },
      textError: { color: STOP },
      sizeSmall: { padding: '5px 12px', fontSize: '0.8125rem' },
      sizeLarge: { padding: '11px 22px', fontSize: '0.9375rem' },
    },
  },

  MuiIconButton: {
    styleOverrides: {
      root: {
        borderRadius: RADIUS_CONTROL,
        color: SUB,
        transition: 'background-color 150ms ease, color 150ms ease',
        '&:hover': { backgroundColor: NEUTRAL },
      },
      // root 가 color 를 잡으므로 의미색 슬롯을 명시적으로 되살린다.
      colorInherit: { color: 'inherit' },
      colorPrimary: { color: ACCENT },
      colorSecondary: { color: STEEL },
      colorInfo: { color: STEEL },
      colorSuccess: { color: SIGNAL },
      colorWarning: { color: '#B07A23' },
      colorError: { color: STOP },
    },
  },

  MuiPaper: {
    defaultProps: { elevation: 0 },
    styleOverrides: {
      root: {
        backgroundImage: 'none',
        borderRadius: RADIUS_SURFACE,
      },
      outlined: {
        border: `1px solid ${BORDER}`,
      },
    },
  },

  MuiCard: {
    defaultProps: { elevation: 0 },
    styleOverrides: {
      root: {
        borderRadius: RADIUS_SURFACE,
        border: `1px solid ${BORDER}`,
        backgroundColor: SURFACE,
        boxShadow: SHADOW_XS,
      },
    },
  },

  MuiCardContent: {
    styleOverrides: {
      root: {
        padding: 20,
        '&:last-child': { paddingBottom: 20 },
      },
    },
  },

  MuiCardActionArea: {
    styleOverrides: {
      root: {
        borderRadius: RADIUS_SURFACE,
        '& .MuiCardActionArea-focusHighlight': { background: 'transparent' },
      },
    },
  },

  MuiOutlinedInput: {
    styleOverrides: {
      root: {
        borderRadius: RADIUS_CONTROL,
        backgroundColor: SURFACE,
        fontFamily: SANS,
        '& .MuiOutlinedInput-notchedOutline': {
          borderColor: BORDER,
          borderWidth: 1,
          transition: 'border-color 150ms ease',
        },
        '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: BORDER_STRONG },
        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
          borderColor: ACCENT,
          borderWidth: 1.5,
        },
        '&.Mui-error .MuiOutlinedInput-notchedOutline': { borderColor: STOP },
        '&.Mui-disabled': { backgroundColor: FIELD },
        '&.Mui-disabled .MuiOutlinedInput-notchedOutline': { borderColor: BORDER },
      },
      input: {
        padding: '10px 12px',
        '&::placeholder': { color: CAPTION, opacity: 1 },
      },
    },
  },

  MuiInputLabel: {
    styleOverrides: {
      root: {
        fontFamily: SANS,
        fontWeight: 500,
        color: SUB,
        '&.Mui-focused': { color: ACCENT },
      },
    },
  },

  MuiFormHelperText: {
    styleOverrides: {
      root: {
        fontFamily: SANS,
        fontSize: '0.75rem',
        color: SUB,
        '&.Mui-error': { color: STOP },
      },
    },
  },

  MuiTextField: {
    defaultProps: { variant: 'outlined' },
  },

  MuiSelect: {
    styleOverrides: {
      select: { padding: '10px 12px' },
      icon: { color: SUB },
    },
  },

  MuiMenu: {
    styleOverrides: {
      paper: {
        borderRadius: RADIUS_MENU,
        border: `1px solid ${BORDER}`,
        boxShadow: SHADOW_POP,
        marginTop: 4,
      },
    },
  },

  MuiMenuItem: {
    styleOverrides: {
      root: {
        fontFamily: SANS,
        fontSize: '0.875rem',
        color: INK,
        minHeight: 38,
        marginLeft: 6,
        marginRight: 6,
        borderRadius: RADIUS_CONTROL,
        '&:hover': { backgroundColor: NEUTRAL },
        '&.Mui-selected': {
          backgroundColor: ACCENT_TINT,
          color: ACCENT_HOVER,
          '&:hover': { backgroundColor: ACCENT_TINT },
        },
      },
    },
  },

  MuiAutocomplete: {
    styleOverrides: {
      paper: {
        borderRadius: RADIUS_MENU,
        border: `1px solid ${BORDER}`,
        boxShadow: SHADOW_POP,
      },
      option: {
        fontFamily: SANS,
        fontSize: '0.875rem',
        borderRadius: RADIUS_CONTROL,
        margin: '2px 6px',
      },
    },
  },

  MuiChip: {
    styleOverrides: {
      root: {
        borderRadius: RADIUS_CHIP,
        fontFamily: SANS,
        fontWeight: 500,
        border: 'none',
      },
      sizeSmall: { borderRadius: 6 },
      outlined: {
        backgroundColor: SURFACE,
        border: `1px solid ${BORDER}`,
        color: INK,
      },
      filledDefault: {
        backgroundColor: NEUTRAL,
        color: INK,
      },
      filledPrimary: {
        backgroundColor: ACCENT,
        color: '#FFFFFF',
      },
    },
  },

  MuiTooltip: {
    styleOverrides: {
      tooltip: {
        borderRadius: 6,
        backgroundColor: INK,
        color: '#FFFFFF',
        fontFamily: SANS,
        fontSize: '0.75rem',
        fontWeight: 500,
        padding: '6px 10px',
        boxShadow: SHADOW_POP,
      },
      arrow: { color: INK },
    },
  },

  MuiAppBar: {
    defaultProps: { elevation: 0 },
    styleOverrides: {
      root: {
        borderRadius: 0,
        backgroundColor: SURFACE,
        color: INK,
        borderBottom: `1px solid ${BORDER}`,
        boxShadow: 'none',
        backgroundImage: 'none',
      },
    },
  },

  MuiToolbar: {
    styleOverrides: {
      root: { minHeight: 56 },
    },
  },

  MuiDrawer: {
    styleOverrides: {
      paper: {
        borderRadius: 0,
        backgroundColor: SURFACE,
        color: INK,
        borderRight: `1px solid ${BORDER}`,
        backgroundImage: 'none',
      },
    },
  },

  MuiListSubheader: {
    styleOverrides: {
      root: {
        backgroundColor: 'transparent',
        fontFamily: SANS,
        fontWeight: 600,
        fontSize: '0.6875rem',
        letterSpacing: '0.08em',
        textTransform: 'none',
        color: CAPTION,
        lineHeight: '32px',
      },
    },
  },

  MuiListItemButton: {
    styleOverrides: {
      root: {
        borderRadius: RADIUS_CONTROL,
        marginBottom: 2,
        transition: 'background-color 150ms ease, color 150ms ease',
        '&:hover': { backgroundColor: NEUTRAL },
        '&.Mui-selected': {
          backgroundColor: ACCENT_TINT,
          color: ACCENT_HOVER,
          '&:hover': { backgroundColor: ACCENT_TINT },
        },
      },
    },
  },

  MuiListItemIcon: {
    styleOverrides: {
      root: { minWidth: 32, color: SUB },
    },
  },

  MuiDivider: {
    styleOverrides: {
      root: { borderColor: BORDER },
    },
  },

  MuiAlert: {
    styleOverrides: {
      root: {
        borderRadius: RADIUS_MENU,
        fontFamily: SANS,
        fontWeight: 500,
      },
      filledError:   { backgroundColor: STOP,   color: '#FFFFFF' },
      filledSuccess: { backgroundColor: SIGNAL, color: '#FFFFFF' },
      filledWarning: { backgroundColor: AMBER,  color: '#1A1208' },
      filledInfo:    { backgroundColor: STEEL,  color: '#FFFFFF' },
    },
  },

  MuiSnackbar: {
    styleOverrides: {
      root: {
        '& .MuiPaper-root': { borderRadius: RADIUS_MENU },
      },
    },
  },

  MuiDialog: {
    styleOverrides: {
      paper: {
        borderRadius: RADIUS_SURFACE,
        border: `1px solid ${BORDER}`,
        boxShadow: SHADOW_DIALOG,
        backgroundImage: 'none',
      },
    },
  },

  MuiDialogTitle: {
    styleOverrides: {
      root: {
        fontFamily: SANS,
        fontWeight: 700,
        fontSize: '1.125rem',
        letterSpacing: '-0.01em',
        color: INK,
        borderBottom: `1px solid ${BORDER}`,
        paddingTop: 16,
        paddingBottom: 12,
      },
    },
  },

  MuiDialogActions: {
    styleOverrides: {
      root: {
        borderTop: `1px solid ${BORDER}`,
        padding: 12,
        gap: 8,
      },
    },
  },

  MuiToggleButton: {
    styleOverrides: {
      root: {
        borderRadius: RADIUS_CONTROL,
        border: `1px solid ${BORDER}`,
        backgroundColor: SURFACE,
        fontFamily: SANS,
        fontWeight: 600,
        textTransform: 'none',
        letterSpacing: 0,
        color: INK,
        '&:hover': { backgroundColor: NEUTRAL },
        '&.Mui-selected': {
          backgroundColor: ACCENT_TINT,
          color: ACCENT_HOVER,
          '&:hover': { backgroundColor: ACCENT_TINT },
        },
      },
    },
  },

  MuiToggleButtonGroup: {
    styleOverrides: {
      grouped: {
        '&:not(:first-of-type)': { borderLeft: `1px solid ${BORDER}`, marginLeft: 0 },
      },
    },
  },

  MuiTabs: {
    styleOverrides: {
      root: { minHeight: 44 },
      indicator: {
        height: 2,
        borderRadius: 2,
        backgroundColor: ACCENT,
      },
    },
  },

  MuiTab: {
    styleOverrides: {
      root: {
        fontFamily: SANS,
        textTransform: 'none',
        fontWeight: 600,
        letterSpacing: 0,
        minHeight: 44,
        color: SUB,
        '&.Mui-selected': { color: INK },
      },
    },
  },

  MuiTableCell: {
    styleOverrides: {
      head: {
        fontFamily: SANS,
        fontWeight: 600,
        fontSize: '0.8125rem',
        letterSpacing: 0,
        textTransform: 'none',
        color: SUB,
        backgroundColor: FIELD,
        borderBottom: `1px solid ${BORDER}`,
      },
      body: {
        fontFamily: SANS,
        fontSize: '0.875rem',
        color: INK,
        borderBottom: `1px solid ${ROW_LINE}`,
      },
    },
  },

  MuiTableContainer: {
    styleOverrides: {
      root: { borderRadius: RADIUS_SURFACE },
    },
  },

  MuiLinearProgress: {
    styleOverrides: {
      root: {
        borderRadius: 999,
        backgroundColor: BORDER,
        height: 6,
      },
      bar: { borderRadius: 999, backgroundColor: ACCENT },
    },
  },

  MuiCircularProgress: {
    styleOverrides: {
      root: { color: ACCENT },
    },
  },

  MuiAvatar: {
    styleOverrides: {
      root: {
        fontFamily: SANS,
        fontWeight: 700,
        fontSize: '0.8125rem',
        backgroundColor: NEUTRAL,
        color: INK,
      },
    },
  },

  MuiBadge: {
    styleOverrides: {
      badge: {
        fontFamily: SANS,
        fontWeight: 700,
        fontSize: '0.6875rem',
        letterSpacing: 0,
      },
    },
  },

  MuiSkeleton: {
    styleOverrides: {
      root: { backgroundColor: NEUTRAL, borderRadius: RADIUS_CONTROL },
    },
  },
};

/* ------------------------------------------------------------------ */
/* MUI X DataGrid — @mui/material 의 Components 타입에는 없으므로       */
/* 별도 오브젝트로 만들어 병합한다.                                     */
/* ------------------------------------------------------------------ */

const dataGridComponents = {
  MuiDataGrid: {
    styleOverrides: {
      root: {
        borderRadius: RADIUS_SURFACE,
        border: `1px solid ${BORDER}`,
        backgroundColor: SURFACE,
        fontFamily: SANS,
        color: INK,
        // DataGrid v7+ CSS 변수 — 헤더/필러 배경과 행 구분선
        '--DataGrid-containerBackground': FIELD,
        '--DataGrid-rowBorderColor': ROW_LINE,
        '--unstable_DataGrid-headWeight': 600,
      },
      columnHeaders: {
        backgroundColor: FIELD,
        borderBottom: `1px solid ${BORDER}`,
      },
      columnHeader: {
        backgroundColor: FIELD,
        '&:focus, &:focus-within': { outline: 'none' },
      },
      // mono 대문자 강제 제거 — 13px / 600 / 서브 잉크
      columnHeaderTitle: {
        fontFamily: SANS,
        fontWeight: 600,
        fontSize: '0.8125rem',
        letterSpacing: 0,
        textTransform: 'none',
        color: SUB,
      },
      columnSeparator: { color: BORDER },
      cell: {
        fontFamily: SANS,
        fontSize: '0.875rem',
        color: INK,
        borderBottom: `1px solid ${ROW_LINE}`,
        '&:focus, &:focus-within': { outline: 'none' },
      },
      row: {
        '&:hover': { backgroundColor: NEUTRAL },
        '&.Mui-selected': {
          backgroundColor: ACCENT_TINT,
          '&:hover': { backgroundColor: ACCENT_TINT },
        },
      },
      footerContainer: {
        backgroundColor: FIELD,
        borderTop: `1px solid ${BORDER}`,
      },
      overlay: { backgroundColor: 'rgba(255,255,255,0.75)' },
      menu: {
        '& .MuiPaper-root': {
          borderRadius: RADIUS_MENU,
          border: `1px solid ${BORDER}`,
          boxShadow: SHADOW_POP,
        },
      },
    },
  },
};

/* ------------------------------------------------------------------ */
/* 테마                                                                */
/* ------------------------------------------------------------------ */

const themeOptions: ThemeOptions = {
  palette,
  shape: { borderRadius: RADIUS_MENU },
  shadows,
  typography: {
    fontFamily: SANS,
    htmlFontSize: 16,
    // 헤딩은 본문과 같은 SANS — mono·uppercase·과한 letter-spacing 강제 없음.
    h1: { fontFamily: SANS, fontWeight: 700, letterSpacing: '-0.01em', lineHeight: 1.2 },
    h2: { fontFamily: SANS, fontWeight: 700, letterSpacing: '-0.01em', lineHeight: 1.25 },
    h3: { fontFamily: SANS, fontWeight: 700, letterSpacing: '-0.01em', lineHeight: 1.3 },
    h4: { fontFamily: SANS, fontWeight: 700, letterSpacing: '-0.01em', lineHeight: 1.3 },
    h5: { fontFamily: SANS, fontWeight: 700, letterSpacing: '-0.005em', lineHeight: 1.35 },
    h6: { fontFamily: SANS, fontWeight: 700, letterSpacing: 0, lineHeight: 1.4 },
    subtitle1: { fontWeight: 600 },
    subtitle2: { fontWeight: 600 },
    body1: { fontSize: '0.9375rem', lineHeight: 1.55 },
    body2: { fontSize: '0.875rem', lineHeight: 1.5 },
    button: { textTransform: 'none' as const, fontWeight: 600, letterSpacing: 0 },
    caption: { fontSize: '0.75rem', letterSpacing: 0, lineHeight: 1.5 },
    overline: {
      fontFamily: SANS,
      fontWeight: 600,
      fontSize: '0.6875rem',
      letterSpacing: '0.06em',
      textTransform: 'none' as const,
      lineHeight: 1.5,
    },
  },
  components: {
    ...components,
    ...dataGridComponents,
  } as ThemeOptions['components'],
};

const theme = createTheme(themeOptions);

// Utility — 원시 토큰이 필요한 컴포넌트를 위한 export.
// (키 이름/형태는 고정 — 앱 전역에서 import 한다. 값만 새 시스템으로 갱신.)
export const designTokens = {
  fonts: { sans: SANS, display: DISPLAY, mono: MONO },
  ink: INK,
  paper: SURFACE,
  concrete: CANVAS,
  parchment: FIELD,
  cargo: ACCENT,
  steel: STEEL,
  amber: AMBER,
  signal: SIGNAL,
  stop: STOP,
};

export default theme;
